import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { parseCallbackSearch } from './OnboardingCallbackPage';
import { recordBulkCallback } from './api';

// Landing route for the BULK authorize OAuth redirect:
//   /onboarding/bulk/callback?profileId=…&tempToken=…&userProfile=… (or
//   ?pendingDataToken=…). The signed-in SPA forwards the params to the authed
//   bulk callback (POST body — token never repeated in a URL on our hop),
//   which establishes the reusable session, then returns to the bulk page.
export function BulkOnboardingCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = parseCallbackSearch(window.location.search);
    const tempToken = params.get('tempToken');
    const pendingDataToken = params.get('pendingDataToken');
    if (!tempToken && !pendingDataToken) {
      setError(
        'The Meta authorize redirect was missing its parameters. Go back and retry the authorize step.',
      );
      return;
    }
    recordBulkCallback({
      tempToken: tempToken ?? undefined,
      pendingDataToken: pendingDataToken ?? undefined,
      profileId: params.get('profileId') ?? undefined,
      userProfile: params.get('userProfile') ?? undefined,
      expiresIn: params.get('expiresIn') ? Number(params.get('expiresIn')) : undefined,
      accountId: params.get('accountId') ?? undefined,
    })
      .then(() => navigate('/onboarding/bulk', { replace: true }))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to establish the bulk session.');
      });
  }, [navigate]);

  return (
    <div className="flex flex-col items-center gap-4 py-24">
      {error ? (
        <>
          <p className="max-w-md text-center text-sm text-rose-600">{error}</p>
          <Button variant="outline" onClick={() => navigate('/onboarding/bulk')}>
            Back to bulk onboarding
          </Button>
        </>
      ) : (
        <>
          <Spinner />
          <p className="text-sm text-slate-500">Authorizing Meta for bulk onboarding…</p>
        </>
      )}
    </div>
  );
}
